import { NavLink } from "react-router-dom";
import { ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import { Icon } from "../../ui/icons";
import { Funnel, TONE_MARKER, type ChartTone } from "../today/charts";
import { useAdvisorDay } from "../today/use-advisor-day";
import type { AdvisorDay } from "../today/types";
import { useRenewalsList, type RenewalStage } from "./use-renewals-list";

// The renewal row's due date, always in Turkish long-short form (same as the
// Müşterilerim book rows) — the surface is Turkish-first regardless of UI locale.
const fmtDue = (iso: string) =>
  new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });

// Stage → the SAME funnel label. The two expired buckets share the funnel's single
// "Süresi geçti" label, so the list and the funnel read identically.
const STAGE_LABEL_KEY: Record<RenewalStage, MessageKey> = {
  T_MINUS_45: "renewals.stage.t45",
  T_MINUS_30: "renewals.stage.t30",
  T_MINUS_15: "renewals.stage.t15",
  T_MINUS_7: "renewals.stage.t7",
  EXPIRY: "renewals.stage.expired",
  POST_EXPIRY: "renewals.stage.expired",
};

// Stage → the SAME tone the cadence funnel uses: amber ONLY at T-7, red ONLY for the
// expired bucket (the genuine-alarm tone); every earlier stage stays neutral. Colour
// never carries the stage alone — the badge pairs the funnel's shape marker with it.
function stageTone(stage: RenewalStage): Extract<ChartTone, "amber" | "neutral" | "red"> {
  if (stage === "T_MINUS_7") return "amber";
  if (stage === "EXPIRY" || stage === "POST_EXPIRY") return "red";
  return "neutral";
}

// Tone → an EXISTING `k-badge` primitive (no new palette): amber = warning, red =
// priority (danger), neutral = standard.
const TONE_BADGE: Record<"amber" | "neutral" | "red", string> = {
  amber: "k-badge--warning",
  neutral: "k-badge--standard",
  red: "k-badge--priority",
};

// YENİLEMELER — the renewals surface. A calm, Turkish-first read of the advisor's
// active renewal cadence, drawn on OUR design system (`k-`/`gunum-` kit + the
// dependency-free Funnel primitive — NO chart library, NO new palette). It reuses
// the composed /v1/metrics/advisor-day read's `renewalPipeline` aggregate: the
// cadence funnel T-45 → T-30 → T-15 → T-7 → (Süresi geçti). Amber ONLY at T-7,
// red ONLY for the expired bucket. Honest-empty on zeros; no fabricated rows.
// COMMISSION APPEARS NOWHERE; no forecast.
//
// Beneath the funnel sits the per-renewal LIST (#222): GET /v1/renewals — the
// own-scope, masked, server-ordered (soonest / expired first) drill-down. Each row
// carries the household label + a stage badge in the funnel's own tone (amber T-7,
// red expired) + the due date + a link into the household 360. The list total
// reconciles with the aggregate above.
//
// The duty-of-care banner is gated on the PRECISE signal `renewalPipeline
// .lapsedUninsured24h` (term lapsed ≤24h AND the asset has no other in-term ACTIVE
// policy) — NOT the broad expired/geçti bucket. So a plain-expired book (older
// lapses, or a re-covered asset) does NOT raise the banner; only a genuine
// lapsed-uninsured-≤24h duty-of-care case does.

export function RenewalsPage() {
  const { t } = useT();
  const { data, isLoading, error, refetch, isFetching } = useAdvisorDay();

  return (
    <section className="surface" data-testid="renewals">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("renewals.heading")}</h1>
          <p className="surface__subtitle">{t("renewals.subtitle")}</p>
        </div>
      </header>

      {isLoading && (
        <div className="k-loading" data-testid="renewals-loading">
          <div className="k-loading__head">
            <span className="k-loading__spinner" />
            <span className="k-loading__label">{t("renewals.loading")}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="k-note k-note--error" data-testid="renewals-error">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--error">
              <Icon name="alert-triangle" size={20} />
            </span>
            <div>
              <p className="k-note__title k-note__title--sm">{t("renewals.error.title")}</p>
              <p role="alert" className="k-note__desc">
                {error instanceof ProblemError ? error.problem.detail : t("renewals.error.detail")}
              </p>
              <p className="k-note__desc gunum-reassure">{t("renewals.error.reassure")}</p>
              <div className="k-note__actions">
                <button
                  type="button"
                  className="k-btn k-btn--soft k-btn--sm"
                  disabled={isFetching}
                  onClick={() => void refetch()}
                  data-testid="renewals-retry"
                >
                  {t("renewals.retry")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {data && <RenewalsBody pipeline={data.renewalPipeline} />}
    </section>
  );
}

function RenewalsBody({ pipeline }: { pipeline: AdvisorDay["renewalPipeline"] }) {
  const { t } = useT();
  const { byStage, total } = pipeline;

  // Honest-empty: no active renewal cadence → a calm empty state, never a fabricated
  // funnel. (The Funnel primitive would render a zero track, but on a wholly empty
  // book the honest surface is the empty card, not an all-zero chart.)
  if (total === 0) {
    return (
      <div className="k-empty" data-testid="renewals-empty">
        <div className="k-empty__icon">
          <Icon name="calendar" size={26} />
        </div>
        <p className="k-empty__title">{t("renewals.empty.title")}</p>
        <p className="k-empty__desc">{t("renewals.empty.desc")}</p>
      </div>
    );
  }

  // The expired/geçti bucket combines EXPIRY (at expiry) + POST_EXPIRY (already
  // lapsed) — the same sum the read exposes as `pipeline.expiry`. It drives the
  // funnel's red tone, NOT the duty-of-care banner.
  const expired = byStage.EXPIRY + byStage.POST_EXPIRY;

  // The PRECISE duty-of-care count: renewals lapsed ≤24h whose asset has no other
  // in-term ACTIVE policy (uninsured). A strict subset of `expired` — the banner
  // fires on THIS, never on the broad expired bucket.
  const dutyOfCare = pipeline.lapsedUninsured24h;

  // The cadence funnel: five calm stages. Amber ONLY at T-7; red ONLY for the
  // expired bucket. Everything else is a neutral track — colour never carries a
  // stage alone (the Funnel pairs a shape marker with each tone).
  const rows: { key: string; tone: ChartTone; label: string; value: number }[] = [
    { key: "t45", tone: "neutral", label: t("renewals.stage.t45"), value: byStage.T_MINUS_45 },
    { key: "t30", tone: "neutral", label: t("renewals.stage.t30"), value: byStage.T_MINUS_30 },
    { key: "t15", tone: "neutral", label: t("renewals.stage.t15"), value: byStage.T_MINUS_15 },
    { key: "t7", tone: "amber", label: t("renewals.stage.t7"), value: byStage.T_MINUS_7 },
    {
      key: "expired",
      tone: expired > 0 ? "red" : "neutral",
      label: t("renewals.stage.expired"),
      value: expired,
    },
  ];

  return (
    <>
      {/* Duty-of-care: a calm-but-serious RED banner for the PRECISE lapsed-
          uninsured-≤24h signal (`pipeline.lapsedUninsured24h`) — a genuine safety
          reach, never the broad expired bucket. If nothing lapsed uninsured within
          24h, the banner stays down even when older/expired renewals exist. */}
      {dutyOfCare > 0 && (
        <div className="k-note k-note--error" data-testid="renewals-duty">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--error">
              <Icon name="alert-triangle" size={20} />
            </span>
            <div>
              <p className="k-note__title k-note__title--sm">{t("renewals.duty.title")}</p>
              <p role="alert" className="k-note__desc">
                {t("renewals.duty.desc")}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="k-card gunum-widget" data-testid="w-cadence">
        <div className="gunum-widget__head">
          <span className="k-stat__icon k-stat__icon--blue">
            <Icon name="calendar" size={18} />
          </span>
          <span className="gunum-widget__title">{t("renewals.cadence.title")}</span>
        </div>

        <Funnel
          rows={rows}
          ariaLabel={`${t("renewals.cadence.title")}: ${rows
            .map((r) => `${r.label} ${r.value}`)
            .join(", ")}`}
          testId="cadence-funnel"
        />

        <p className="gunum-note">
          <span className="gunum-stat__value" data-testid="renewals-total">
            {total}
          </span>{" "}
          {t("renewals.total")}
        </p>

        {/* No-silent-lapse guarantee. */}
        <p
          className={`gunum-note ${expired > 0 ? "gunum-note--amber" : ""}`}
          data-testid="renewals-note"
        >
          {t("renewals.note")}
        </p>
      </div>

      {/* The per-renewal drill-down (#222): a masked, server-ordered row list beneath
          the aggregate. Its counts reconcile with the funnel above. */}
      <RenewalsList />
    </>
  );
}

// The per-renewal row list — GET /v1/renewals (#222), own-scope + masked, ordered by
// the server (soonest / expired first; never re-sorted here). Each row carries the
// household label, a stage badge in the funnel's own tone, the due date, and a link
// into the household 360. Loading skeleton, calm error + retry, honest-empty, and a
// "Daha fazla" pager. NO commission, NO forecast.
function RenewalsList() {
  const { t } = useT();
  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useRenewalsList();

  if (isLoading) {
    return (
      <div className="k-loading" data-testid="renewals-list-loading">
        <div className="k-loading__head">
          <span className="k-loading__spinner" />
          <span className="k-loading__label">{t("renewals.list.loading")}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k-note k-note--error" data-testid="renewals-list-error">
        <div className="k-note__row">
          <span className="k-note__icon k-note__icon--error">
            <Icon name="alert-triangle" size={20} />
          </span>
          <div>
            <p role="alert" className="k-note__desc">
              {error instanceof ProblemError ? error.problem.detail : t("renewals.list.error")}
            </p>
            <p className="k-note__desc gunum-reassure">{t("renewals.error.reassure")}</p>
            <div className="k-note__actions">
              <button
                type="button"
                className="k-btn k-btn--soft k-btn--sm"
                disabled={isFetching}
                onClick={() => void refetch()}
                data-testid="renewals-list-retry"
              >
                {t("renewals.retry")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const rows = data?.pages.flatMap((p) => p?.data ?? []) ?? [];

  return (
    <div className="k-card gunum-widget" data-testid="renewals-list">
      <div className="gunum-widget__head">
        <span className="k-stat__icon k-stat__icon--blue">
          <Icon name="calendar" size={18} />
        </span>
        <span className="gunum-widget__title">{t("renewals.list.title")}</span>
      </div>

      {rows.length === 0 ? (
        <div className="k-empty" data-testid="renewals-list-empty">
          <div className="k-empty__icon">
            <Icon name="calendar" size={26} />
          </div>
          <p className="k-empty__title">{t("renewals.list.empty")}</p>
        </div>
      ) : (
        <>
          <ul className="book-list">
            {rows.map((row) => {
              const tone = stageTone(row.stage);
              return (
                <li
                  key={row.id}
                  className="k-card k-card--pad-sm book-row"
                  data-testid={`renewal-row-${row.id}`}
                >
                  <div className="book-row__main">
                    <NavLink to={`/households/${row.householdId}`} className="book-link">
                      {row.householdName}
                    </NavLink>
                  </div>
                  <span
                    className={`k-badge ${TONE_BADGE[tone]}`}
                    data-testid={`renewal-stage-${row.id}`}
                  >
                    <span aria-hidden="true">{TONE_MARKER[tone]} </span>
                    {t(STAGE_LABEL_KEY[row.stage])}
                  </span>
                  <span className="k-chip">
                    <Icon name="calendar" size={14} />
                    {fmtDue(row.dueDate)}
                  </span>
                </li>
              );
            })}
          </ul>

          {hasNextPage && (
            <div className="k-note__actions">
              <button
                type="button"
                className="k-btn k-btn--soft k-btn--sm"
                disabled={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
                data-testid="renewals-list-more"
              >
                {t("renewals.list.more")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
